import { useMemo, useState } from "react";
import { buildPayload, commandForm } from "../../lib/commandFields";
import { Button } from "../ui/Button";
import { Field, SelectField } from "../ui/Input";
import { MicroLabel } from "../ui/Text";

/**
 * Remote commands.
 *
 * ## The command list comes from the server
 *
 * `availableCommands` is the server's own safe set (spec 4.2). This component
 * renders whatever is in it and hardcodes nothing, so a command that is removed
 * server-side disappears here rather than becoming a button that 400s.
 *
 * ## Fields are described, not free-form JSON
 *
 * A textarea for the payload would be faster to write and would put an operator
 * one typo away from a malformed frame. Each command's fields are declared in
 * `lib/commandFields.ts`, per OCPP version, with their types; anything not
 * declared sends `{}`, which is correct for the commands that take no arguments.
 *
 * ## Destructive commands confirm
 *
 * `Reset` power-cycles a charger that may have a car attached, and
 * `UnlockConnector` releases a physical latch. Both require a second press, and
 * the confirm state names the station so a mis-click on the wrong tab is caught.
 */

/** Commands that touch hardware state a driver would notice. */
const NEEDS_CONFIRMATION = new Set(["Reset", "UnlockConnector", "ChangeAvailability"]);

export function CommandPanel({
  commands,
  ocppVersion,
  connected,
  pending,
  onSend,
}: {
  readonly commands: readonly string[];
  /** Which version's form to show; command names collide across versions. */
  readonly ocppVersion: string | null;
  readonly connected: boolean;
  readonly pending: boolean;
  readonly onSend: (input: {
    command: string;
    payload: Record<string, unknown>;
  }) => void;
}) {
  const [command, setCommand] = useState<string>(commands[0] ?? "");
  const [values, setValues] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);

  const form = commandForm(ocppVersion, command);
  const fields = form.fields;

  /**
   * Defaults are applied at read time rather than being written into state when
   * the command changes. That keeps a single source of truth — an untouched field
   * has no entry in `values` — and avoids an effect that syncs state to state.
   */
  const current = useMemo(() => {
    const merged: Record<string, string> = {};
    for (const field of fields) {
      merged[field.name] = values[field.name] ?? field.defaultValue ?? "";
    }
    return merged;
  }, [fields, values]);

  const missing = fields.filter(
    (field) => field.required === true && (current[field.name] ?? "") === "",
  );
  const canSend = connected && !pending && missing.length === 0 && command !== "";

  function selectCommand(next: string) {
    setCommand(next);
    // Values do not carry across commands: `connectorId` means something
    // different on UnlockConnector than on ChangeAvailability, and a stale
    // `idTag` silently riding along would be worse than retyping it.
    setValues({});
    setConfirming(false);
  }

  function submit() {
    if (!canSend) return;

    if (NEEDS_CONFIRMATION.has(command) && !confirming) {
      setConfirming(true);
      return;
    }

    setConfirming(false);
    onSend({ command, payload: buildPayload(form, current) });
  }

  return (
    <div className="flex flex-col gap-5 border-t border-rule pt-4">
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3">
        <SelectField
          label="Command"
          value={command}
          onChange={(event) => selectCommand(event.target.value)}
        >
          {commands.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </SelectField>

        {fields.map((field) =>
          field.type === "enum" ? (
            <SelectField
              key={field.name}
              label={field.label}
              value={current[field.name] ?? ""}
              onChange={(event) => {
                setValues((previous) => ({ ...previous, [field.name]: event.target.value }));
                setConfirming(false);
              }}
            >
              {(field.options ?? []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectField>
          ) : (
            <Field
              key={field.name}
              label={field.label}
              type={field.type === "number" ? "number" : "text"}
              inputMode={field.type === "number" ? "numeric" : undefined}
              value={current[field.name] ?? ""}
              onChange={(event) => {
                setValues((previous) => ({ ...previous, [field.name]: event.target.value }));
                setConfirming(false);
              }}
              {...(field.hint === undefined ? {} : { hint: field.hint })}
            />
          ),
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Button
          variant={confirming ? "danger" : "primary"}
          disabled={!canSend}
          pending={pending}
          onClick={submit}
        >
          {pending ? "Sending" : confirming ? `Confirm ${command}` : "Send"}
        </Button>

        {confirming ? (
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <span className="text-body text-signal-ink">
              This affects the physical station. Press confirm to send.
            </span>
          </>
        ) : null}

        {/*
          Only what is blocking the press right now.

          "Commands need a live connection — the server answers 409…" used to sit
          here permanently, and the section heading already says "Not connected"
          while the button is already disabled. Three statements of one fact, one of
          them a full sentence set in tracked uppercase, is how a panel teaches its
          operators to skip captions. The 30-second-ceiling and never-retried notes
          that followed are the same problem: they matter only after a command has
          timed out, so they are said there (`ChargePointDetail`) instead of here.
        */}
        {connected && missing.length > 0 ? (
          <MicroLabel>Required: {missing.map((field) => field.label).join(", ")}</MicroLabel>
        ) : null}
      </div>
    </div>
  );
}
